import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import { Box, Typography } from '@mui/material';

// 메인화면 비주얼 리뉴얼(2026-10-23) - 메인화면 카드에서는 큰 월간 달력 대신
// "이번 주 일정" 아젠다 목록을 보여준다(전체 월간/주간/일간 달력은 "전체 보기"로 넘어가는
// 화면쪽 DashboardCalendar.js가 그대로 맡는다 - item 5).
//
// 일정 종류별 점 색 - 백엔드(ScheduleMapper.selectScheduleList)가 지금은 "tech"(기술지원)만
// 내려준다(PMS_TECH_PLN 기반). "영업"/"휴가·근태" 구분은 스키마에 아직 없어서(ScheduleDTO 주석의
// mock 예시에만 'vacation' 등이 있다) 색 매핑은 미리 마련해 두되 실제로는 기술지원 색만 보인다 -
// report에 기록.
const TYPE_COLOR = {
  tech: 'warning.main', // 기술지원
  sales: 'info.main', // 영업(데이터 없음 - 대비만 해둠)
  vacation: 'text.disabled', // 휴가·근태(데이터 없음 - 대비만 해둠)
  leave: 'text.disabled',
};
const typeColor = (type) => TYPE_COLOR[type] || 'primary.main';

const WEEKDAY_KR = ['일', '월', '화', '수', '목', '금', '토'];

const startOfThisWeek = () => {
  const today = dayjs();
  const dow = today.day(); // 0=일 ~ 6=토
  const diffToMonday = dow === 0 ? -6 : 1 - dow;
  return today.add(diffToMonday, 'day').startOf('day');
};

const eventsOnDay = (events, day) => {
  const dayStart = day;
  const dayEndExclusive = day.add(1, 'day');
  return events.filter((ev) => {
    if (!ev.start) return false;
    const evStart = dayjs(ev.start);
    const evEnd = ev.end ? dayjs(ev.end) : evStart;
    return evStart.isBefore(dayEndExclusive) && evEnd.isAfter(dayStart);
  });
};

// handelEventClick(DashboardCalendarContainer)이 FullCalendar의 이벤트 클릭 핸들러 모양
// (`e.event.extendedProps`, `e.event.title`)을 기대하므로 같은 모양으로 감싸서 넘긴다 -
// 기존 "기술지원 상세" 모달 동작을 그대로 쓴다.
const toFakeClickEvent = (raw) => ({ event: { title: raw.title, extendedProps: raw } });

const WeekAgenda = ({ events = [], onEventClick }) => {
  const days = useMemo(() => {
    const monday = startOfThisWeek();
    return Array.from({ length: 7 }, (_, i) => monday.add(i, 'day'));
  }, []);

  const today = dayjs().startOf('day');

  return (
    <Box>
      {days.map((day) => {
        const dayEvents = eventsOnDay(events, day);
        const isToday = day.isSame(today, 'day');
        const isWeekend = day.day() === 0 || day.day() === 6;
        return (
          <Box
            key={day.format('YYYY-MM-DD')}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.25,
              py: 0.75,
              borderBottom: '1px solid',
              borderColor: 'divider',
              '&:last-of-type': { borderBottom: 0 },
            }}
          >
            <Box sx={{ width: 44, flexShrink: 0, textAlign: 'center' }}>
              <Typography
                variant="caption"
                component="div"
                sx={{
                  color: isWeekend ? 'text.disabled' : 'text.secondary',
                  fontWeight: isToday ? 700 : 400,
                }}
              >
                {WEEKDAY_KR[day.day()]}
              </Typography>
              <Typography
                variant="body2"
                component="div"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  fontWeight: isToday ? 700 : 400,
                  color: isToday ? 'common.white' : isWeekend ? 'text.disabled' : 'text.primary',
                  bgcolor: isToday ? 'primary.main' : 'transparent',
                }}
              >
                {day.format('D')}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, pt: 0.25 }}>
              {dayEvents.length === 0 ? (
                <Typography variant="caption" color="text.disabled">
                  일정 없음
                </Typography>
              ) : (
                dayEvents.slice(0, 3).map((ev) => (
                  <Box
                    key={ev.id ?? ev.taskUuid ?? ev.title}
                    onClick={() => onEventClick && onEventClick(toFakeClickEvent(ev))}
                    // 접근성(재검토 2, 2026-10-23 §6-13): 클릭만 되는 Box는 role/tabIndex가 없어
                    // 키보드로는 포커스도, 실행도 안 됐다 - onEventClick이 있을 때만 버튼
                    // 시맨틱(role/tabIndex/키보드 Enter·Space)을 준다.
                    role={onEventClick ? 'button' : undefined}
                    tabIndex={onEventClick ? 0 : undefined}
                    aria-label={onEventClick ? `${ev.title} 일정 상세 보기` : undefined}
                    onKeyDown={(e) => {
                      if (!onEventClick) return;
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onEventClick(toFakeClickEvent(ev));
                      }
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      cursor: onEventClick ? 'pointer' : 'default',
                      mb: 0.25,
                      '&:hover': onEventClick ? { textDecoration: 'underline' } : undefined,
                      '&:focus-visible': onEventClick
                        ? { outline: '2px solid', outlineColor: 'primary.main', borderRadius: 0.5 }
                        : undefined,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: typeColor(ev.type), flexShrink: 0 }}
                    />
                    <Typography variant="body2" noWrap>
                      {ev.title}
                    </Typography>
                    {ev.usrNm && (
                      <Typography variant="caption" color="text.secondary" noWrap>
                        · {ev.usrNm}
                      </Typography>
                    )}
                  </Box>
                ))
              )}
              {dayEvents.length > 3 && (
                <Typography variant="caption" color="text.secondary">
                  +{dayEvents.length - 3}건 더
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default WeekAgenda;
